import React, {useContext} from 'react'
import {cn} from '@/lib/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {BuilderContext} from '@builder.io/sdk-react'
import {accountFormLabelInput} from '../account-drawer/constants/accountFormLabelInput'
import {CategoryProductGrid, categoryProductGridPerPageLimitInputField} from './categoryProductGrid'
import {injectedGridContentItemsDefinition} from './constants/injectedGridContent'
import {getStandardBreadcrumbInputs, breadcrumbTypes} from '../breadcrumb/utils'
import {StandardBreadcrumb} from '../breadcrumb/standardBreadcrumb'
import {DrawerFilters} from '../DrawerFilters/DrawerFilters'
import SortPopover from '../../sort/sortPopover'
import {useCioFacetSelector} from '@/context/cio-facet'
import {CategoryFilters} from '../../../components/filters/filters'
import {PlpBuilderBlockProps, BANNER_TYPES, layoutTypeEnum} from './types'
import {useAutoCategoryBreadcrumbs} from 'hec/builder/hooks/useAutoCategoryBreadcrumbs'
import {usePlpServerData} from 'hec/context/plp-server-data'

const PlpBuilderBlock = ({
    attributes,
    perPageLimit,
    accountFormLabels,
    injectedContentItems,
    customCrumbs,
    categoryCrumbs,
    separator,
    includeHome,
    builderBlock,
    showFilters,
    override,
    filterType,
    list,
    links,
    facets,
    layoutType,
    iconSizeSmall,
    iconSizeLarge
}: PlpBuilderBlockProps) => {
    const config = getConfig()
    const {
        app: {threeLetter}
    } = config
    const {className, restAttributes} = attributes || {}
    const cioFacetContext = useCioFacetSelector((context) => context)
    const TotalResults = cioFacetContext?.state?.totalResults?.responseTotal || 0

    const builderContext = useContext(BuilderContext)
    const categoryId = builderContext?.content?.data?.productCategory?.options?.category
    const {categoryName: clientCategoryName} = useAutoCategoryBreadcrumbs({
        isEnabled: !!categoryId,
        categoryId
    })
    const {serverCategoryName} = usePlpServerData()
    const categoryName = serverCategoryName ?? clientCategoryName

    return (
        <div
            {...restAttributes}
            className={cn('p-3 lg:p-0 lg:container flex flex-col ', className)}
            data-testid="plp-builder-block"
        >
            {showFilters && (
                <CategoryFilters
                    className={className}
                    attributes={attributes}
                    iconSizes={{large: iconSizeLarge, small: iconSizeSmall}}
                    list={list}
                    links={links}
                    facets={facets}
                    filterType={filterType}
                    layoutType={layoutType}
                    data-testid={'filters-CategoryFilters'}
                />
            )}

            <StandardBreadcrumb
                type={breadcrumbTypes.CATEGORY}
                customCrumbs={customCrumbs || []}
                categoryCrumbs={categoryCrumbs || []}
                separator={separator || '>'}
                builderBlock={builderBlock}
                includeHome={includeHome || true}
                data-testid={'plpBuilderBlock-StandardBreadcrumb'}
            />

            <div className="grid grid-cols-[auto_1fr_auto] mt-3">
                {categoryName && (
                    <h1 className="plp-h1 text-2xl font-serif text-center col-span-3 lg:col-span-1 lg:col-start-2 lg:row-start-1">
                        {categoryName}
                    </h1>
                )}
                <div className="col-start-1 row-start-2 lg:row-start-1 lg:row-span-2 self-center">
                    <DrawerFilters data-testid={'plpBuilderBlock-DrawerFilters'} />
                </div>
                <div className="col-start-2 row-start-2 lg:row-start-2 text-center self-center">
                    {TotalResults} Products – All {threeLetter} Eligible
                </div>
                {/*  SortPopover has some weird restriction called isRequiredComponentPresent i think its
                when the plp was composed of different components? I created a small override flag  */}
                <div className="col-start-3 row-start-2 lg:row-start-1 lg:row-span-2 self-center">
                    <SortPopover override={override} data-testid={'plpBuilderBlock-SortPopover'} />
                </div>
            </div>
            <div className="w-full">
                <CategoryProductGrid
                    accountFormLabels={accountFormLabels}
                    perPageLimit={perPageLimit || 60}
                    injectedContentItems={injectedContentItems}
                    data-testid={'plpBuilderBlock-CategoryProductGrid'}
                />
            </div>
        </div>
    )
}

export const PlpBuilderBlockDefinition = {
    component: PlpBuilderBlock,
    name: 'PlpBuilderBlock',
    noWrap: true,
    inputs: [
        categoryProductGridPerPageLimitInputField,
        accountFormLabelInput,
        injectedGridContentItemsDefinition,
        ...getStandardBreadcrumbInputs(),
        {
            name: 'showFilters',
            type: 'boolean',
            defaultValue: true,
            helperText:
                'Show filters in the PLP page (will only work if product category is set to a parent category)'
        },
        {
            name: 'override',
            type: 'boolean',
            defaultValue: false,
            helperText: 'Override the sibling component check in the sortPopover component'
        },
        {
            name: 'filterType',
            type: 'string',
            required: true,
            enum: Object.values(BANNER_TYPES),
            defaultValue: BANNER_TYPES.FILTERS,
            showIf: 'options.get("showFilters") === true'
        },
        {
            name: 'layoutType',
            type: 'string',
            enum: Object.values(layoutTypeEnum),
            defaultValue: layoutTypeEnum.scroll,
            helperText: 'Choose how the filters will be displayed',
            showIf: 'options.get("showFilters") === true'
        },
        {
            name: 'iconSizeSmall',
            type: 'string',
            showIf: 'options.get("showFilters") === true',
            enum: ['110px', '120px', '130px', '140px', '150px'],
            defaultValue: '110px',
            helperText: 'Size of the icon for small screens'
        },
        {
            name: 'iconSizeLarge',
            type: 'string',
            showIf: 'options.get("showFilters") === true',
            enum: ['110px', '120px', '130px', '140px', '150px'],
            defaultValue: '110px',
            helperText: 'Size of the icon for large screens'
        },
        {
            name: 'list',
            type: 'list',
            showIf: `options.get('filterType') === '${BANNER_TYPES.FILTERS}'`,
            subFields: [
                {
                    name: 'Filter Label',
                    type: 'string',
                    defaultValue: 'Under $X'
                },
                {
                    name: 'Maximum Price',
                    type: 'string',
                    defaultValue: 'inf',
                    helperText: 'Sets pmax=x param, leave empty to ignore'
                },
                {
                    name: 'Minimum Price',
                    type: 'string',
                    defaultValue: '0',
                    helperText: 'Sets pmin=x param, leave empty to ignore'
                },
                {
                    name: 'Search parameters',
                    type: 'string',
                    defaultValue: '',
                    helperText:
                        'Parameters to append to the URL when clicking the filter link. Format: "key1=value1&key2=value2"'
                }
            ]
        },
        {
            name: 'facets',
            type: 'list',
            helperText:
                'Configure which CIO facets to display. Use the facet "name" field (e.g. "brand", "category").',
            defaultValue: [
                {name: 'productType', value: 'bundle', display_name: ''},
                {name: 'productType', value: 'multi-pack', display_name: ''}
            ],
            subFields: [
                {
                    name: 'name',
                    type: 'string',
                    required: true,
                    helperText: 'The CIO facet key (e.g. "brand", "category")'
                },
                {
                    name: 'value',
                    type: 'string',
                    required: true,
                    helperText: 'The option value to pre-select (e.g. "nike")'
                },
                {
                    name: 'display_name',
                    type: 'string',
                    helperText:
                        'Optional label override. Falls back to the CIO option display name.'
                }
            ]
        },
        {
            name: 'links',
            type: 'list',
            showIf: `options.get('filterType') === '${BANNER_TYPES.LINK_AND_IMAGE}'`,
            subFields: [
                {
                    name: 'Label',
                    type: 'string',
                    defaultValue: ''
                },
                {
                    name: 'Link',
                    type: 'string',
                    defaultValue: '',
                    helperText:
                        'Absolute URL or relative path to the filter link. Example: "/category?filter=value"'
                },
                {
                    name: 'Image',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'png']
                }
            ]
        }
    ]
}
